import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText, Rocket, Bike, GaugeCircle, Map, Star, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation
    FaTools,            // For Engine & Gearbox Protection
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaFileInvoice,      // For Return to Invoice
    FaHardHat,          // For Helmet & Riding Gear Cover
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaUserShield,
    FaTire,
    FaTired,            // For Tyre Protection
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HarleyDavidsonBikeInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is Harley-Davidson bike insurance mandatory in India?",
      answer: "Yes, at least third-party insurance is legally required.",
    },
    {
      question: "What’s the best insurance type for a Harley cruiser?",
      answer: "Comprehensive cover with add-ons like RSA, zero depreciation, and engine protection is best.",
    },
    {
      question: "Are Harley parts expensive to repair or replace?",
      answer: "Yes, hence insurance becomes even more crucial for financial protection.",
    },
    {
      question: "Can I get online insurance for a Harley?",
      answer: "Yes, TheFinMaster helps you compare and buy Harley insurance completely online.",
    },
    {
      question: "Does insurance cover long-distance touring incidents?",
      answer: "Yes, as long as the ride isn’t for racing or commercial purposes.",
    },
    {
      question: "How much is the premium for Harley X440?",
      answer: "Premiums start around ₹2,800 and vary based on rider and location.",
    },
    {
      question: "Does Harley insurance cover custom modifications?",
      answer: "Only if declared and approved during policy issuance. Always disclose customizations.",
    },
    {
      question: "Is Roadside Assistance available for Harley bikes?",
      answer: "Yes, and highly recommended for long-distance and highway riders.",
    },
    {
      question: "Can I renew Harley insurance after it expires?",
      answer: "Yes, but try to renew before expiry to avoid inspection and NCB loss.",
    },
    {
      question: "Does NCB (No Claim Bonus) apply to premium bikes?",
      answer: "Yes, and can save you up to 50% on premiums over time.",
    },
    {
      question: "Can I transfer my NCB to another Harley or brand?",
      answer: "Yes, NCB is tied to the rider, not the bike.",
    },
    {
      question: "What if my Harley is stolen? Will insurance help?",
      answer: "A comprehensive plan with RTI will reimburse full invoice value in most cases.",
    },
    {
      question: "Does insurance cover accessories like saddlebags or speakers?",
      answer: "Only if they are declared and valued in the policy.",
    },
    {
      question: "Is engine damage during rains covered?",
      answer: "Only if you opt for the Engine Protection add-on.",
    },
    {
      question: "Can I insure imported Harley models?",
      answer: "Yes, but you may need specialized coverage for CBU imports.",
    },
    {
      question: "Do I need a separate cover for riding gear?",
      answer: "Yes, a Helmet and Gear Add-On covers your jackets, gloves, and helmets.",
    },
    {
      question: "Will claims take longer for premium bikes?",
      answer: "Not necessarily. Reputed insurers process Harley claims quickly if documents are in order.",
    },
    {
      question: "Can I port my Harley insurance to another insurer?",
      answer: "Yes, at the time of renewal you can switch and retain NCB.",
    },
    {
      question: "Does Harley bike insurance include off-road coverage?",
      answer: "Standard plans don’t. If you use your Harley off-road, ask for special endorsements.",
    },
    {
      question: "How can I get the best Harley-Davidson insurance deals?",
      answer: "Use TheFinMaster to compare plans from top insurers and customize them with add-ons.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Receive full claim value without depreciation deductions.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine & Gearbox Protection",
      description: "Useful for accidental oil leaks or water damage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance (RSA)",
      description: "Great for tourers, includes fuel delivery, towing, and on-spot repairs.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Return to Invoice Cover",
      description: "Receive full invoice value in case of total loss or theft.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers minor parts like nuts, bolts, oil, lubricants, etc.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Helmet & Riding Gear Cover",
      description: "Ideal for Harley riders who invest in premium safety gear.",
      icon: <FaHardHat className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Tyre Protection Cover",
      description: "Protects against unexpected damage to tyres and tubes.",
      icon: <FaTired className="text-4xl text-gray-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];

  const benefits = [
    {
      title: "Harley-Davidson X440",
      desc: "Entry-level roadster built in collaboration with Hero.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Iron 883",
      desc: "Classic cruiser with a raw, stripped-down appeal.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Forty-Eight",
      desc: "Bold design and high-torque V-Twin for urban cruising.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Fat Bob 114",
      desc: "Aggressive styling and big-displacement torque.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "Sportster S",
      desc: "Modernized power cruiser with performance DNA.",
      icon: <Star className="w-7 h-7 text-yellow-500" />,
      gradient: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Heritage Classic",
      desc: "Long-distance cruiser with modern touring features.",
      icon: <Zap className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "Street Glide Special / Road Glide Special",
      desc: "High-end baggers designed for highway comfort and style.",
      icon: <Shield className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100",
    },
  ];
const harleyinsurance = [
    {
      name: "X440",
      path: "/harley-davidson-x440-bike-insurance", 
      image: "/assets/harleydavidsonx440.jpeg",
    },
    {
      name: "Nightster",
      path: "/harley-davidson-nightster-bike-insurance", 
      image: "/assets/harleydavidsonnightster.jpeg",
    },
   {
      name: "Sportster S",
      path: "/harley-davidson-sportster-s-bike-insurance", 
      image: "/assets/harleydavidsonsportsters.jpeg",
    },
    {
      name: "Fatboy 114",
      path: "/harley-davidson-fat-boy-114-bike-insurance", 
      image: "/assets/harleydavidsonfatboy114.jpeg",
    },
    {
      name: "Heritage Classic114",
      path: "/harley-davidson-heritage-classic-114-bike-insurance", 
      image: "/assets/harleydavidsonheritage114.jpeg",
    },
     {
      name: "Street Glide",
      path: "/harley-davidson-street-glide-bike-insurance", 
      image: "/assets/harleydavidsonstreetglide.jpeg",
    },
      {
      name: "Road Glide",
      path: "/harley-davidson-road-glide-bike-insurance", 
      image: "/assets/harleydavidsonroadglide.jpeg",
    },

  ];
  return (
    <>
      <Helmet>
        <title>Harley-Davidson Bike Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Harley-Davidson bike insurance options in India. Compare quotes, customize coverage, and buy online to protect your Harley X440, Iron 883, or Street Glide."
        />
        <meta
          name="keywords"
          content="harley-davidson bike insurance, harley x440 insurance, harley iron 883 insurance, two-wheeler insurance quotes"
        />
          <link rel="canonical" href="https://thefinmaster.com/harley-davidson-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/harley-davidson-bike-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Harley-Davidson Bike Insurance in India" />
        <meta property="og:description" content="Compare and buy Harley-Davidson bike insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/harley-davidson-bike-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Harley-Davidson Bike Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Harley-Davidson Bike Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Harley-Davidson is an icon in the world of motorcycling, known for its distinctive style, powerful engines, and unmatched road presence. Whether you ride a classic Street 750 or a touring-ready Fat Boy, keeping your bike protected starts with the right insurance plan. This guide walks you through everything you need to know about insuring Harley-Davidson, from premiums and model insights to useful add-ons, renewal tips, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/harleyinsurance.jpeg"
                alt="Harley-Davidson Bike"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About Harley-Davidson Bikes in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Harley-Davidson, the legendary American motorcycle brand, offers a distinct range of cruisers and roadsters in India. Since 2020, Harley-Davidson has partnered with Hero MotoCorp for its India operations, ensuring continued availability, servicing, and support.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="p-4 sm:p-8 bg-white">
                                      <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                        Choose Your Bike Brand
                                      </h2>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {harleyinsurance.map((calc, index) => (
                                          <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.04 }}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: index * 0.05 }}
                                          >
                                            <Link
                                              to={calc.path}
                                              className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                            >
                                              <img
                                                src={calc.image}
                                                alt={calc.name}
                                                className="w-16 h-16 object-contain mb-2"
                                              />
                                              <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                {calc.name}
                                              </span>
                                            </Link>
                                          </motion.div>
                                        ))}
                                      </div>
                                    </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Harley Owners Need Comprehensive Bike Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Harley-Davidson bikes are premium machines with custom parts, imported components, and high engine capacities. Here's why comprehensive insurance is essential:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Legal compliance with Indian motor vehicle laws</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Covers accidental damage, natural disasters, fire, and theft</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Safeguards expensive repairs and proprietary parts</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Protects your investment against unforeseen financial losses</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>Offers peace of mind while touring or cruising long distances</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your Harley stays protected, legally compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
          <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
             <div className="flex flex-col md:flex-row justify-between items-center gap-10">
               <div className="max-w-xl space-y-6 text-center md:text-left">
                 <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                   Calculate Your TwoWheeler Insurance <br />
                   in <span className="text-blue-600">Seconds</span>
                 </h2>
                 <p className="text-gray-700 text-lg">
                   Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
                 </p>
                 <Link
                   to="/financial-tools-calculators/bike-insurance-calculator"
                   className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
                 >
                   Start Calculating <ArrowRight className="w-5 h-5" />
                 </Link>
               </div>
               <div className="space-y-4 text-left">
                 {features.map((item, index) => (
                   <div key={index} className="flex items-center gap-3">
                     <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                     <span className="text-gray-800 font-medium">{item.text}</span>
                   </div>
                 ))}
               </div>
             </div>
           </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Harley-Davidson Models & Insurance Premium Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Note: Premiums may vary based on location, IDV, add-ons selected, and riding history.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Harley-Davidson X440", category: "Entry Roadster", idv: "₹2.40 – ₹2.70 Lakh", premium: "₹2,800 – ₹3,300" },
              { model: "Iron 883", category: "Cruiser", idv: "₹11.00 – ₹12.00 Lakh", premium: "₹7,500 – ₹9,000" },
              { model: "Forty-Eight", category: "Mid Cruiser", idv: "₹13.00 – ₹14.00 Lakh", premium: "₹9,000 – ₹11,500" },
              { model: "Sportster S", category: "Power Cruiser", idv: "₹16.50 – ₹17.50 Lakh", premium: "₹11,000 – ₹14,000" },
              { model: "Fat Bob 114", category: "High-End Cruiser", idv: "₹21.00 – ₹23.00 Lakh", premium: "₹14,000 – ₹17,000" },
              { model: "Heritage Classic", category: "Touring Cruiser", idv: "₹25.00 – ₹26.50 Lakh", premium: "₹16,000 – ₹19,000" },
              { model: "Street Glide Special", category: "Touring Bagger", idv: "₹33.00 – ₹34.00 Lakh", premium: "₹20,000 – ₹25,000" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">Segment</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Harley-Davidson X440", "Entry Roadster", "₹2.40 – ₹2.70 Lakh", "₹2,800 – ₹3,300"],
                  ["Iron 883", "Cruiser", "₹11.00 – ₹12.00 Lakh", "₹7,500 – ₹9,000"],
                  ["Forty-Eight", "Mid Cruiser", "₹13.00 – ₹14.00 Lakh", "₹9,000 – ₹11,500"],
                  ["Sportster S", "Power Cruiser", "₹16.50 – ₹17.50 Lakh", "₹11,000 – ₹14,000"],
                  ["Fat Bob 114", "High-End Cruiser", "₹21.00 – ₹23.00 Lakh", "₹14,000 – ₹17,000"],
                  ["Heritage Classic", "Touring Cruiser", "₹25.00 – ₹26.50 Lakh", "₹16,000 – ₹19,000"],
                  ["Street Glide Special", "Touring Bagger", "₹33.00 – ₹34.00 Lakh", "₹20,000 – ₹25,000"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Harley Bike Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Due to the premium nature of Harley bikes, the following add-ons can greatly enhance your policy’s protection:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurance Providers for Harley-Davidson in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Opt for insurers who understand the premium bike segment and offer cashless repairs, high IDV coverage, and fast settlement:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">TATA AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Offers smart policy options for Harley bikes.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Reliable coverage with quick claim settlements.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Known for wide garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Trusted insurer with Harley-compatible garage network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Seamless app experience and cashless claims.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Hassle-free digital claims process.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🛡️</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Reliance General Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Reliable coverage with digital claims support.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required to Insure a Harley-Davidson Bike
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To buy or renew your insurance, you’ll need:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Owner’s ID proof (Aadhaar, PAN, etc.)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Passport-sized photo</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance details (if applicable)</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Invoice copy for new bikes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About Harley-Davidson Bike Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a Harley-Davidson is about lifestyle, passion, and individuality. But that also comes with the responsibility of protecting your machine from financial loss and damage. A strong insurance policy ensures that your Harley, whether it’s the nimble X440 or a grand Street Glide Special, is safeguarded at every turn.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Head over to TheFinMaster to compare Harley-Davidson bike insurance plans, choose the ideal add-ons, and enjoy a seamless, paperless buying experience that’s as smooth as your ride.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default HarleyDavidsonBikeInsurance;